import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Badge,
  Table
} from 'reactstrap';
import {
  FaHistory,
  FaBox,
  FaUser,
  FaCalendarAlt,
  FaFileAlt,
  FaMapMarkerAlt,
  FaArrowUp,
  FaArrowDown,
  FaExchangeAlt,
  FaAdjust,
  FaBoxes
} from 'react-icons/fa';
import { formatPrice } from '../../helpers/utility';

const MouvementStockDetails = ({ mouvement, isOpen, toggle }) => {
  const { t } = useTranslation();

  if (!mouvement) return null;

  const getTypeMouvementIcon = (type) => {
    switch (type) {
      case 'ENTREE':
      case 'RETOUR_CLIENT':
        return <FaArrowUp className="text-success" />;
      case 'SORTIE':
      case 'PERTE':
        return <FaArrowDown className="text-danger" />;
      case 'TRANSFERT':
        return <FaExchangeAlt className="text-info" />;
      case 'AJUSTEMENT':
        return <FaAdjust className="text-warning" />;
      case 'INVENTAIRE':
        return <FaBoxes className="text-primary" />;
      default:
        return <FaHistory className="text-secondary" />;
    }
  };

  const getTypeMouvementBadge = (type) => {
    const config = {
      'ENTREE': { color: 'success', label: t('mouvement.entree') },
      'SORTIE': { color: 'danger', label: t('mouvement.sortie') },
      'TRANSFERT': { color: 'info', label: t('mouvement.transfert') },
      'AJUSTEMENT': { color: 'warning', label: t('mouvement.ajustement') },
      'INVENTAIRE': { color: 'primary', label: t('mouvement.inventaire') },
      'PERTE': { color: 'dark', label: t('mouvement.perte') },
      'RETOUR_CLIENT': { color: 'success', label: t('mouvement.retourClient') }
    };

    const typeConfig = config[type] || { color: 'secondary', label: type };
    
    return (
      <Badge color={typeConfig.color} className="d-flex align-items-center">
        {getTypeMouvementIcon(type)}
        <span className="ms-2">{typeConfig.label}</span>
      </Badge>
    );
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return '-';
    return new Date(dateTime).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const calculateVariation = () => {
    if (!mouvement.quantiteAvant || !mouvement.quantiteApres) return null;
    return mouvement.quantiteApres - mouvement.quantiteAvant;
  };

  const calculateValorisation = () => {
    if (!mouvement.prixUnitaire || !mouvement.quantite) return null;
    return mouvement.prixUnitaire * mouvement.quantite;
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        <div className="d-flex align-items-center">
          <FaHistory className="text-primary me-2" />
          {t('mouvement.detailsMouvement')} #{mouvement.id}
        </div>
      </ModalHeader>

      <ModalBody>
        {/* Informations générales */}
        <Card className="mb-3">
          <CardHeader className="bg-light">
            <h6 className="mb-0">
              <FaFileAlt className="me-2" />
              {t('mouvement.informationsGenerales')}
            </h6>
          </CardHeader>
          <CardBody>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label text-muted">{t('mouvement.typeMouvement')}</label>
                  <div>{getTypeMouvementBadge(mouvement.typeMouvement)}</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label text-muted">{t('mouvement.dateMouvement')}</label>
                  <div className="d-flex align-items-center">
                    <FaCalendarAlt className="text-muted me-2" />
                    {formatDateTime(mouvement.dateMouvement)}
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label text-muted">{t('mouvement.utilisateur')}</label>
                  <div className="d-flex align-items-center">
                    <FaUser className="text-muted me-2" />
                    <Badge color="secondary" pill>
                      {mouvement.utilisateur || 'Système'}
                    </Badge>
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label text-muted">{t('mouvement.referenceDocument')}</label>
                  <div>
                    {mouvement.referenceDocument ? (
                      <Badge color="outline-primary">
                        {mouvement.referenceDocument}
                      </Badge>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Informations article */}
        <Card className="mb-3">
          <CardHeader className="bg-light">
            <h6 className="mb-0">
              <FaBox className="me-2" />
              {t('mouvement.informationsArticle')}
            </h6>
          </CardHeader>
          <CardBody>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label text-muted">{t('mouvement.codeArticle')}</label>
                  <div><strong>{mouvement.articleCode}</strong></div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label text-muted">{t('mouvement.nomArticle')}</label>
                  <div>{mouvement.articleNom}</div>
                </div>
              </Col>
              {mouvement.gestionParLots && (
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">{t('mouvement.numeroLot')}</label>
                    <div>
                      <Badge color="info">
                        {mouvement.numeroLot}
                      </Badge>
                    </div>
                  </div>
                </Col>
              )}
              {mouvement.emplacement && (
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">{t('mouvement.emplacement')}</label>
                    <div className="d-flex align-items-center">
                      <FaMapMarkerAlt className="text-muted me-2" />
                      {mouvement.emplacement}
                    </div>
                  </div>
                </Col>
              )}
            </Row>
          </CardBody>
        </Card>

        {/* Informations quantités */}
        <Card className="mb-3">
          <CardHeader className="bg-light">
            <h6 className="mb-0">
              <FaAdjust className="me-2" />
              {t('mouvement.informationsQuantites')}
            </h6>
          </CardHeader>
          <CardBody>
            <Table bordered size="sm">
              <tbody>
                <tr>
                  <td className="bg-light"><strong>{t('mouvement.quantiteAvant')}</strong></td>
                  <td className="text-end">
                    {formatPrice(mouvement.quantiteAvant, false)}
                  </td>
                </tr>
                <tr>
                  <td className="bg-light"><strong>{t('mouvement.quantiteMouvement')}</strong></td>
                  <td className="text-end">
                    <span className={
                      ['ENTREE', 'RETOUR_CLIENT'].includes(mouvement.typeMouvement) 
                        ? 'text-success' 
                        : ['SORTIE', 'PERTE'].includes(mouvement.typeMouvement)
                        ? 'text-danger'
                        : 'text-info'
                    }>
                      <strong>
                        {['ENTREE', 'RETOUR_CLIENT'].includes(mouvement.typeMouvement) ? '+' : 
                         ['SORTIE', 'PERTE'].includes(mouvement.typeMouvement) ? '-' : ''}
                        {formatPrice(mouvement.quantite, false)}
                      </strong>
                    </span>
                  </td>
                </tr>
                <tr className="table-primary">
                  <td><strong>{t('mouvement.quantiteApres')}</strong></td>
                  <td className="text-end">
                    <strong>{formatPrice(mouvement.quantiteApres, false)}</strong>
                  </td>
                </tr>
                {calculateVariation() !== null && (
                  <tr>
                    <td className="bg-light"><strong>{t('mouvement.variation')}</strong></td>
                    <td className="text-end">
                      <Badge color={calculateVariation() >= 0 ? 'success' : 'danger'}>
                        {calculateVariation() >= 0 ? '+' : ''}{formatPrice(calculateVariation(), false)}
                      </Badge>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </CardBody>
        </Card>

        {/* Informations financières */}
        {mouvement.prixUnitaire && (
          <Card className="mb-3">
            <CardHeader className="bg-light">
              <h6 className="mb-0">
                <FaFileAlt className="me-2" />
                {t('mouvement.informationsFinancieres')}
              </h6>
            </CardHeader>
            <CardBody>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">{t('mouvement.prixUnitaire')}</label>
                    <div><strong>{formatPrice(mouvement.prixUnitaire)} DH</strong></div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted">{t('mouvement.valorisation')}</label>
                    <div>
                      <Badge color="info" className="fs-6">
                        {formatPrice(calculateValorisation())} DH
                      </Badge>
                    </div>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        )}

        {/* Motif */}
        {mouvement.motif && (
          <Card>
            <CardHeader className="bg-light">
              <h6 className="mb-0">
                <FaFileAlt className="me-2" />
                {t('mouvement.motif')}
              </h6>
            </CardHeader>
            <CardBody>
              <div className="bg-light p-3 rounded">
                <em>{mouvement.motif}</em>
              </div>
            </CardBody>
          </Card>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          {t('common.close')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default MouvementStockDetails;
